import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Loader2, ChevronLeft, Mail, MessageCircle, Send, LifeBuoy, CheckCircle2, Clock } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const TEAL = "#0891B2";
const CATS = ["general", "pagos", "marketplace", "academia", "software", "cuenta"];
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function SoportePage() {
  const [form, setForm] = useState({ asunto: "", categoria: "general", mensaje: "" });
  const [enviando, setEnviando] = useState(false);
  const [tickets, setTickets] = useState([]);

  const cargar = () => api.get("/soporte/mis-tickets").then(({ data }) => setTickets(data.items || [])).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.asunto.trim() || form.mensaje.trim().length < 10) { toast.error("Escribe un asunto y un mensaje de al menos 10 caracteres."); return; }
    setEnviando(true);
    try {
      await api.post("/soporte", form);
      toast.success("Tu consulta fue enviada. Te responderemos por correo.");
      setForm({ asunto: "", categoria: "general", mensaje: "" });
      await cargar();
    } catch (err) { toast.error(humanizeAxiosError(err)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="px-5 md:px-8 py-8 max-w-3xl" data-testid="soporte-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al Ecosistema</Link>
      <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3"><Headphones size={30} style={{ color: TEAL }} /> Soporte</h1>
      <p className="mt-2 text-slate-600">Estamos para ayudarte. Escríbenos y te responderemos lo antes posible.</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <a href="mailto:soporte@construlink.pe" data-testid="soporte-email" className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 hover:border-slate-300 transition-colors">
          <Mail size={20} style={{ color: TEAL }} /><p className="mt-2 font-bold text-slate-900 text-sm">Correo</p><p className="text-xs text-slate-500">soporte@construlink.pe</p>
        </a>
        <a href="https://wa.me/51999999999" target="_blank" rel="noreferrer" data-testid="soporte-whatsapp" className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 hover:border-slate-300 transition-colors">
          <MessageCircle size={20} className="text-emerald-500" /><p className="mt-2 font-bold text-slate-900 text-sm">WhatsApp</p><p className="text-xs text-slate-500">Chat directo</p>
        </a>
        <Link to="/centro-ayuda" data-testid="soporte-ayuda" className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 hover:border-slate-300 transition-colors">
          <LifeBuoy size={20} className="text-slate-500" /><p className="mt-2 font-bold text-slate-900 text-sm">Centro de Ayuda</p><p className="text-xs text-slate-500">Preguntas frecuentes</p>
        </Link>
      </div>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="soporte-form-card">
        <h2 className="font-bold text-slate-900">Enviar una consulta</h2>
        <form onSubmit={enviar} className="mt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.asunto} onChange={(e) => setForm((f) => ({ ...f, asunto: e.target.value }))} data-testid="soporte-asunto" placeholder="Asunto" className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
            <select value={form.categoria} onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))} data-testid="soporte-categoria" className="rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-cyan-200">
              {CATS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          </div>
          <textarea value={form.mensaje} onChange={(e) => setForm((f) => ({ ...f, mensaje: e.target.value }))} data-testid="soporte-mensaje" rows={4} placeholder="Cuéntanos en qué podemos ayudarte…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200" />
          <button type="submit" disabled={enviando} data-testid="soporte-enviar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: TEAL }}>
            {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Enviar consulta
          </button>
        </form>
      </section>

      {tickets.length > 0 && (
        <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="soporte-tickets-card">
          <h2 className="font-bold text-slate-900">Mis consultas</h2>
          <div className="mt-3 divide-y divide-slate-100">
            {tickets.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-3" data-testid={`soporte-ticket-${t.id}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.estado === "abierto" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                  {t.estado === "abierto" ? <Clock size={15} /> : <CheckCircle2 size={15} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.asunto}</p>
                  <p className="text-xs text-slate-500 capitalize">{t.categoria} · {fecha(t.created_at)}</p>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{t.estado}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
